import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Play, Wrench, AlertTriangle, BookOpen,
  CheckCircle, Phone, Lock, Video, FileQuestion, ChevronRight
} from 'lucide-react';

const features = [
  {
    icon: Video,
    title: 'Interactive Training Videos',
    desc: "Custom video content built for your equipment and SOPs. Step-by-step walkthroughs your crew can watch on-site.",
    examples: ['Power tongs setup & operation', 'Thread washing procedures', 'Torque monitoring systems'],
  },
  {
    icon: FileQuestion,
    title: 'Troubleshooting Guides',
    desc: "Visual, step-by-step diagnostic guides so your crew can fix common issues without calling the shop.",
    examples: ["Equipment won't start checklists", 'Hydraulic pressure diagnostics', 'Error code lookups'],
  },
];

const benefits = [
  'Reduce equipment downtime by 40%',
  'Onboard new hires in days, not weeks',
  'Custom content built for YOUR company',
  'Available on any device, even offline',
];

export default function TrainingDashboard() {
  const navigate = useNavigate();

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        
      </div>

      {/* Sub-header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Training & Resources</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-5">
        {/* Platform Help Videos — always visible */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
            <Video className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Help & Training Videos</p>
            <p className="text-xs text-fieldiq-text mt-0.5">Platform walkthroughs and troubleshooting guides from FieldIQ</p>
          </div>
          <button
            onClick={() => navigate('/help-videos')}
            className="flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg bg-fieldiq-accent text-black hover:bg-amber-400 transition-colors shrink-0"
          >
            <Play className="w-3.5 h-3.5" /> Browse Videos
          </button>
        </div>

        {/* Hero */}
        <div className="bg-gradient-to-br from-fieldiq-accent/20 to-fieldiq-accent/5 border border-fieldiq-accent/30 rounded-xl p-5 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-fieldiq-accent/20 flex items-center justify-center mx-auto">
            <Sparkles className="w-6 h-6 text-fieldiq-accent" />
          </div>
          <h2 className="text-base font-bold text-white">Upgrade to Training & Resources</h2>
          <p className="text-xs text-fieldiq-text leading-relaxed max-w-sm mx-auto">
            We'll build custom interactive training videos and troubleshooting guides tailored to your equipment and procedures.
          </p>
          <span className="inline-flex items-center gap-1 text-xs bg-fieldiq-card text-fieldiq-text px-3 py-1 rounded-full">
            <Lock className="w-3 h-3" /> Premium Add-on
          </span>
        </div>

        {/* Benefits */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2.5">
          {benefits.map((b) => (
            <div key={b} className="flex items-center gap-2.5">
              <CheckCircle className="w-4 h-4 text-fieldiq-accent shrink-0" />
              <span className="text-xs font-medium text-white">{b}</span>
            </div>
          ))}
        </div>

        {/* Feature Cards */}
        <section className="space-y-3">
          <h2 className="text-sm font-bold text-white">What's Included</h2>
          {features.map((f) => (
            <div key={f.title} className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2.5">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-fieldiq-accent/15 flex items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4 text-fieldiq-accent" />
                </div>
                <p className="text-sm font-semibold text-white">{f.title}</p>
              </div>
              <p className="text-xs text-fieldiq-text leading-relaxed">{f.desc}</p>
              <div className="space-y-1.5 pl-1">
                {f.examples.map((ex) => (
                  <div key={ex} className="flex items-center gap-2 text-xs text-fieldiq-muted">
                    <ChevronRight className="w-3 h-3 shrink-0" />
                    <span>{ex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Pricing */}
        <div className="bg-fieldiq-card border border-fieldiq-accent/30 rounded-xl p-5 text-center space-y-3">
          <p className="text-xs text-fieldiq-muted uppercase tracking-wider font-semibold">Training Add-on</p>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-3xl font-bold text-fieldiq-accent">$35</span>
            <span className="text-xs text-fieldiq-muted">/user/month</span>
          </div>
          <p className="text-xs text-fieldiq-muted">Includes custom content creation for your company</p>
          <div className="flex gap-2 pt-1">
            <button className="flex-1 flex items-center justify-center gap-1 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold py-2.5 rounded-lg hover:bg-amber-400 transition-colors">
              <Sparkles className="w-3.5 h-3.5" /> Upgrade Now
            </button>
            <button className="flex items-center justify-center gap-1 bg-fieldiq-card border border-fieldiq-border text-white text-xs font-medium py-2.5 px-4 rounded-lg hover:border-fieldiq-accent transition-colors">
              <Phone className="w-3.5 h-3.5" /> Contact Sales
            </button>
          </div>
        </div>

        {/* Preview Teaser */}
        <section className="space-y-2">
          <h2 className="text-sm font-bold text-white">Preview Sample Content</h2>
          <p className="text-[10px] text-fieldiq-muted">See what your crew would get access to</p>
          {[
            { title: 'Power Tongs Operation', duration: '45 min', icon: Wrench },
            { title: 'H2S Safety Awareness', duration: '60 min', icon: AlertTriangle },
            { title: 'Equipment Handover Checklist', duration: '8 min', icon: BookOpen },
          ].map((item) => (
            <div key={item.title} className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3 flex items-center gap-3 opacity-75">
              <div className="w-10 h-10 rounded-lg bg-fieldiq-dark flex items-center justify-center shrink-0 relative">
                <Play className="w-4 h-4 text-fieldiq-muted" />
                <Lock className="w-3 h-3 text-fieldiq-muted absolute -top-1 -right-1" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white">{item.title}</p>
                <p className="text-[10px] text-fieldiq-muted">{item.duration} · Locked</p>
              </div>
              <span className="text-[10px] border border-fieldiq-border text-fieldiq-muted px-2 py-0.5 rounded-full flex items-center gap-0.5 shrink-0">
                <Lock className="w-2.5 h-2.5" /> Premium
              </span>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
